import moment from "moment";
import cns from "classnames";
import { useFormField } from "@clientUtils";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

import locale, { dateFormat } from "@bonliva-crm/locale";
import { ConsultantQueryBookingStatus } from "@bonliva-crm/graphql";
import { Color, DatePicker, RadioButtonGroup, SelectMenu } from "@bonliva-crm/ui";

import { BookingStatusFilterData } from "./utils";
import { CollapsibleFavFilter } from "../_base/collapsible-fav-filter";
import { FilterWithFavouriteProps } from "../../utils/filters-record-types";

type Option = { label: string; value: ConsultantQueryBookingStatus };

export const BookingStatusFilter = (props: FilterWithFavouriteProps) => {
  const classes = useStyles();
  const {
    setFieldValue: _setFieldValue,
    field,
    fieldState,
    formContext: { resetField }
  } = useFormField<BookingStatusFilterData>(props.name);

  const { field: bookingStatusOption } = useFormField<string>("bookingStatusOption");

  const clear = () => {
    resetField(props.name);
  };

  const setFieldValue = (
    status: ConsultantQueryBookingStatus,
    start: Date | null,
    end: Date | null
  ) => {
    if (!bookingStatusOption.value) {
      bookingStatusOption.onChange("all");
    }

    _setFieldValue(
      {
        bookingStatus: status,
        bookingStatusDate: start,
        bookingStatusDateEnd: end
      },
      { shouldDirty: true }
    );
  };

  const options = Object.values(ConsultantQueryBookingStatus).map<Option>((status) => ({
    label: locale.consultantQueryBookingStatus[status],
    value: status
  }));

  const handleBookingStatusChange = (_: any, v: string) => {
    if (v === field.value?.bookingStatus) {
      return clear();
    }

    if (v === ConsultantQueryBookingStatus.NoBookings) {
      return setFieldValue(v, null, null);
    }

    if (
      v !== ConsultantQueryBookingStatus.BookingsInPeriod &&
      v !== ConsultantQueryBookingStatus.BookingsInPeriodButNotAfter
    ) {
      return setFieldValue(
        v as ConsultantQueryBookingStatus,
        field.value?.bookingStatusDate,
        null
      );
    }

    return setFieldValue(
      v,
      field.value?.bookingStatusDate,
      field.value?.bookingStatusDateEnd
    );
  };

  const showStartDatePicker = [
    ConsultantQueryBookingStatus.HasBookingsFromDate,
    ConsultantQueryBookingStatus.NoBookingsFromDate,
    ConsultantQueryBookingStatus.NoBookingsFromDateBookedPreviously,
    ConsultantQueryBookingStatus.NoBookingsInPeriod,
    ConsultantQueryBookingStatus.BookingsInPeriod,
    ConsultantQueryBookingStatus.BookingsInPeriodButNotAfter,
    ConsultantQueryBookingStatus.NoBookingsBeforeDate
  ].includes(field.value?.bookingStatus);

  const showEndDatePicker = [
    ConsultantQueryBookingStatus.BookingsInPeriod,
    ConsultantQueryBookingStatus.BookingsInPeriodButNotAfter,
    ConsultantQueryBookingStatus.NoBookingsInPeriod
  ].includes(field.value?.bookingStatus);

  const radioButtonGroup = (
    <RadioButtonGroup
      name="bookingStatus"
      options={options}
      value={field.value?.bookingStatus}
      onChange={handleBookingStatusChange}
    />
  );

  // The period pickers bound each other so an inverted range cannot be selected.
  // An inverted range covers no weeks, which used to reach Mongo as an empty
  // `$search` operand and fail the whole consultant search (CRMDEV-7166). The
  // bound only applies to the statuses that actually use both dates.
  const fromPicker = () => (
    <DatePicker
      fullWidth
      isClearable
      value={field?.value?.bookingStatusDate ?? null}
      onChange={(v) => {
        setFieldValue(field.value?.bookingStatus, v, field.value?.bookingStatusDateEnd);
      }}
      label={locale.StartDate}
      DatePickerProps={{
        maxDate:
          showEndDatePicker && field.value?.bookingStatusDateEnd
            ? field.value.bookingStatusDateEnd
            : undefined
      }}
    />
  );

  const toPicker = () => (
    <DatePicker
      fullWidth
      isClearable
      value={field?.value?.bookingStatusDateEnd ?? null}
      onChange={(v) => {
        setFieldValue(field.value?.bookingStatus, field.value?.bookingStatusDate, v);
      }}
      label={locale.EndDate}
      DatePickerProps={{ minDate: field.value?.bookingStatusDate ?? undefined }}
    />
  );

  // slider view
  if (props.setFavorite) {
    return (
      <CollapsibleFavFilter
        title={props.label ?? locale.BookingStatus}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        isClearable={fieldState.isDirty}
        onClear={clear}
        isDirty={fieldState.isDirty}
        initialExpanded={fieldState.isDirty}
      >
        <Box flex="1">
          <ToggleButtonGroup
            value={bookingStatusOption?.value}
            exclusive
            onChange={(e, v) => {
              e.preventDefault();
              e.stopPropagation();
              bookingStatusOption.onChange(v);
            }}
          >
            <ToggleButton value="all">{locale.All}</ToggleButton>
            <ToggleButton value="national">{locale.NPOption}</ToggleButton>
            <ToggleButton value="not_national">{locale.NoNPOption}</ToggleButton>
          </ToggleButtonGroup>

          <Box>{radioButtonGroup}</Box>

          {(showStartDatePicker || showEndDatePicker) && (
            <Box style={{ marginTop: 16, display: "flex" }}>
              {showStartDatePicker && (
                <Box style={{ flex: "0 0 45%" }}>{fromPicker()}</Box>
              )}

              {showEndDatePicker && (
                <Box style={{ flex: "0 0 45%", paddingLeft: "8px" }}>{toPicker()}</Box>
              )}
            </Box>
          )}
        </Box>
      </CollapsibleFavFilter>
    );
  }

  // favourite (dropdown) view

  const placeholderText = field.value?.bookingStatus
    ? getPlaceholderText(
        field.value?.bookingStatus,
        field.value?.bookingStatusDate,
        field.value?.bookingStatusDateEnd
      )
    : props.label ?? locale.BookingStatus;

  return (
    <div className={cns(classes.root, classes.border)}>
      <SelectMenu
        openByDefault={false}
        isClearable={fieldState.isDirty}
        badgeLabel={fieldState.isDirty ? "Ändrad" : undefined}
        placeholder={
          <Typography variant="body1" style={{ color: Color.DetailsInput }}>
            {placeholderText}
          </Typography>
        }
        showPlaceholderOnExpand
        onClear={() => clear()}
        content={
          <Box flex="1">
            <Box>{radioButtonGroup}</Box>
            {showStartDatePicker && <Box pt={2}>{fromPicker()}</Box>}
            {showEndDatePicker && <Box pt={2}>{toPicker()}</Box>}
          </Box>
        }
      />

      <div>
        <ToggleButtonGroup
          size="small"
          value={bookingStatusOption?.value}
          exclusive
          onChange={(e, v) => {
            bookingStatusOption.onChange(v);
          }}
        >
          <ToggleButton value="all">{locale.All}</ToggleButton>
          <ToggleButton value="national">{locale.NPOption}</ToggleButton>
          <ToggleButton value="not_national">{locale.NoNPOption}</ToggleButton>
        </ToggleButtonGroup>
      </div>
    </div>
  );
};

function getPlaceholderText(
  status: ConsultantQueryBookingStatus,
  fromDate: Date | null,
  toDate: Date | null
) {
  const from = fromDate ? moment(fromDate).format(dateFormat) : "";
  const to = toDate ? moment(toDate).format(dateFormat) : "";

  switch (status) {
    case ConsultantQueryBookingStatus.BookingsInPeriod:
      return locale.consultantQueryBookingStatusStr.BookingsInPeriod(from, to);
    case ConsultantQueryBookingStatus.BookingsInPeriodButNotAfter:
      return locale.consultantQueryBookingStatusStr.BookingsInPeriodButNotAfter(from, to);
    case ConsultantQueryBookingStatus.NoBookingsBeforeDate:
      return locale.consultantQueryBookingStatusStr.NoBookingsBeforeDate(from);
    case ConsultantQueryBookingStatus.HasBookingsFromDate:
      return locale.consultantQueryBookingStatusStr.HasBookingsFromDate(from);
    case ConsultantQueryBookingStatus.NoBookings:
      return locale.consultantQueryBookingStatusStr.NoBookings();
    case ConsultantQueryBookingStatus.NoBookingsFromDate:
      return locale.consultantQueryBookingStatusStr.NoBookingsFromDate(from);
    case ConsultantQueryBookingStatus.NoBookingsFromDateBookedPreviously:
      return locale.consultantQueryBookingStatusStr.NoBookingsFromDateBookedPreviously(
        from
      );
    default:
      return locale.consultantQueryBookingStatus[status];
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    gap: "0.5rem"
  },
  border: {
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "4px"
  }
}));
